/**
 * Định nghĩa toàn bộ route ứng dụng React Router.
 *
 * Luồng dữ liệu:
 * - HomeLayout: trang chủ (featured + tìm kiếm qua query string).
 * - PublicLayout: chi tiết BĐS, profile, favorites, auth redirect.
 * - AdminLayout: khu vực admin bọc ProtectedRoute (role admin/superAdmin).
 * - PropertyListRedirect: chuyển /properties → / với cùng query (legacy URL).
 */
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ProtectedRoute } from '../components/Auth/ProtectedRoute';
import { AdminLayout } from '../layouts/AdminLayout';
import { HomeLayout } from '../layouts/HomeLayout';
import { PublicLayout } from '../layouts/PublicLayout';
import { AdminContactsPage } from '../pages/admin/AdminContactsPage';
import { AdminDashboardPage } from '../pages/admin/AdminDashboardPage';
import { AdminPropertiesPage } from '../pages/admin/AdminPropertiesPage';
import { AdminPropertyFormPage } from '../pages/admin/AdminPropertyFormPage';
import { AdminManagedUsersPage } from '../pages/admin/AdminManagedUsersPage';
import { AdminUsersPage } from '../pages/admin/AdminUsersPage';
import { FavoritesPage } from '../pages/public/FavoritesPage';
import { ProfilePage } from '../pages/public/ProfilePage';
import { HomePage } from '../pages/public/HomePage';
import { PropertyDetailPage } from '../pages/public/PropertyDetailPage';
import { LoginRedirect, RegisterRedirect } from './AuthDialogRedirect';
import { APP_ROUTES, ROUTE_PATHS } from '../shared/constants/routes';
import { USER_ROLES } from '../shared/constants/users';

/** Redirect /properties sang trang chủ, giữ nguyên query string tìm kiếm */
const PropertyListRedirect = () => {
  const location = useLocation();

  return <Navigate to={`${APP_ROUTES.home}${location.search}`} replace />;
};

export const AppRoutes = () => (
  <Routes>
    {/* Trang chủ — layout có header search và category bar */}
    <Route element={<HomeLayout />}>
      <Route path={APP_ROUTES.home} element={<HomePage />} />
    </Route>

    {/* Trang công khai — chi tiết tin, profile, auth dialog redirect */}
    <Route element={<PublicLayout />}>
      <Route path={APP_ROUTES.properties} element={<PropertyListRedirect />} />
      <Route path={ROUTE_PATHS.propertyDetails} element={<PropertyDetailPage />} />
      <Route
        path={APP_ROUTES.profile}
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path={APP_ROUTES.favorites}
        element={
          <ProtectedRoute>
            <FavoritesPage />
          </ProtectedRoute>
        }
      />
      <Route path={APP_ROUTES.login} element={<LoginRedirect />} />
      <Route path={APP_ROUTES.register} element={<RegisterRedirect />} />
    </Route>

    {/* Khu admin — yêu cầu role admin trở lên */}
    <Route
      path={APP_ROUTES.admin}
      element={
        <ProtectedRoute requiredRole={USER_ROLES.admin}>
          <AdminLayout />
        </ProtectedRoute>
      }
    >
      <Route index element={<AdminDashboardPage />} />
      <Route path={ROUTE_PATHS.adminProperties} element={<AdminPropertiesPage />} />
      <Route
        path={ROUTE_PATHS.adminPropertyNew}
        element={
          <ProtectedRoute requiredRole={USER_ROLES.admin}>
            <AdminPropertyFormPage />
          </ProtectedRoute>
        }
      />
      <Route path={ROUTE_PATHS.adminPropertyEdit} element={<AdminPropertyFormPage />} />
      <Route path={ROUTE_PATHS.adminContacts} element={<AdminContactsPage />} />
      {/* Chỉ superAdmin quản lý user */}
      <Route
        path={ROUTE_PATHS.adminManagedUsers}
        element={
          <ProtectedRoute requiredRole={USER_ROLES.superAdmin}>
            <AdminManagedUsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTE_PATHS.adminUsers}
        element={
          <ProtectedRoute requiredRole={USER_ROLES.superAdmin}>
            <AdminUsersPage />
          </ProtectedRoute>
        }
      />
    </Route>

    {/* Fallback — mọi URL không khớp quay về trang chủ */}
    <Route path={ROUTE_PATHS.wildcard} element={<Navigate to={APP_ROUTES.home} replace />} />
  </Routes>
);
